import { useEffect, useState } from "react";
import { CalendarClock } from "lucide-react";
import api from "@/lib/api";
import { fmtDay } from "@/components/demo/DemoSlotPicker";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
const when = (b) => `${b.local_date} at ${b.local_time} ${b.tz_abbrev}`;

// Looks up (debounced) whether this work email + website already has an upcoming demo.
export function useExistingBooking(email, website, enabled) {
  const [existing, setExisting] = useState(null);
  useEffect(() => {
    if (!enabled || !EMAIL_RE.test(email || "") || !(website || "").includes(".")) { setExisting(null); return undefined; }
    let live = true;
    const t = setTimeout(() => {
      api.post("/demo/existing", { work_email: email.trim(), website: website.trim() })
        .then((r) => live && setExisting(r.data.booking)).catch(() => {});
    }, 600);
    return () => { live = false; clearTimeout(t); };
  }, [email, website, enabled]);
  return [existing, setExisting];
}

export function ExistingDemoNotice({ existing, kept }) {
  return (
    <div role="status" className="flex gap-3 rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900" data-testid="demo-existing-notice">
      <CalendarClock className="w-5 h-5 shrink-0 mt-0.5" />
      <p>{kept
        ? <>No changes made — your demo stays on <strong>{when(existing)}</strong>.</>
        : <>This email already has a demo booked for <strong>{when(existing)}</strong>. Booking now will change your demo to the time you picked.</>}</p>
    </div>
  );
}

export function MoveDemoDialog({ open, existing, slot, onKeep, onMove }) {
  if (!existing || !slot) return null;
  return (
    <AlertDialog open={open} onOpenChange={(o) => { if (!o) onKeep(); }}>
      <AlertDialogContent data-testid="demo-move-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle>Change your demo time?</AlertDialogTitle>
          <AlertDialogDescription data-testid="demo-move-dialog-text">
            You already have a Cyannect demo booked for <strong>{when(existing)}</strong>. Do you want to move it
            to <strong>{fmtDay(slot.local_date)} at {slot.local_time} {slot.tz_abbrev}</strong>? Your earlier time will be cancelled.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel onClick={onKeep} data-testid="demo-move-keep">Keep my current time</AlertDialogCancel>
          <AlertDialogAction onClick={onMove} data-testid="demo-move-confirm">Yes, change my demo time</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
